import type { Member } from "@/types/Member";

export type MemberInactiveState = "terminated" | "ending" | "inactive";

export interface MemberInactiveStatus {
  state: MemberInactiveState;
  label: string;
  /** ISO date string for when the state began, when known. */
  since?: string;
}

/**
 * Resolve a member to a non-active status, or null if the member is active.
 * `isSuspended` is folded into "terminated" (there is no separate suspended state
 * operationally). Plain `active: false` (no termination/suspension flag) is "inactive".
 */
export function getMemberInactiveStatus(
  member: Member | null | undefined,
): MemberInactiveStatus | null {
  if (!member) return null;

  if (member.isTerminated === true || member.isSuspended === true) {
    return {
      state: "terminated",
      label: "Terminated",
      since: member.terminatedAt ?? member.terminationDate ?? member.suspendedAt,
    };
  }

  // Queued for end-membership: the unified End-Membership flow sets pendingMembershipEnd +
  // membershipEndDate. The member is still active (and bookable/manageable) until the
  // scheduled-terminations job processes them, so this is a distinct state from "inactive".
  if (member.pendingMembershipEnd === true && !!member.membershipEndDate) {
    return { state: "ending", label: "Ending", since: member.membershipEndDate };
  }

  if (member.active === false) {
    return { state: "inactive", label: "Inactive", since: undefined };
  }

  return null;
}

/**
 * The date a member's membership is scheduled to END (inclusive), when they are queued for
 * termination — from EITHER the current End-Membership flow (`pendingMembershipEnd` +
 * `membershipEndDate`) OR the legacy scheduled-termination flow (`scheduledTerminationId` +
 * `terminationDate`). Returns undefined when the member is not pending termination.
 *
 * Used to bound recurring-booking displays (the "Ending {date}" line and the "Manage series"
 * occurrence list) so a series never reads as running past the member's membership end — even when
 * the booking's own recurrence was never truncated (e.g. the booking predates the schedule, or was
 * scheduled via the legacy flow which cancels genesis-after bookings but leaves genesis-before ones
 * unbounded).
 */
export function getMemberTerminationEndDate(
  member:
    | {
        pendingMembershipEnd?: boolean;
        membershipEndDate?: string;
        scheduledTerminationId?: string;
        terminationDate?: string;
      }
    | null
    | undefined,
): string | undefined {
  if (!member) return undefined;
  if (member.pendingMembershipEnd === true && member.membershipEndDate) return member.membershipEndDate;
  if (member.scheduledTerminationId && member.terminationDate) return member.terminationDate;
  return undefined;
}

/**
 * Inactive = the membership has ended (terminated or otherwise deactivated). Drives the greyed,
 * read-only treatment on the view-member page. A merely-queued member (pendingMembershipEnd) is
 * NOT inactive. Legacy `isSuspended` is intentionally excluded (out of scope).
 */
export function isMemberInactive(
  member: Member | null | undefined,
): boolean {
  if (!member) return false;
  return member.active === false || member.isTerminated === true;
}

/**
 * True when the member has reached (or passed) their anonymisation date — `anonymisationDate` is
 * the membership end date + 1 year. A member with no anonymisationDate (legacy / other termination
 * paths) is treated as NOT anonymised. Anonymised members can no longer be reactivated; inactive
 * members who are not yet anonymised remain reactivatable.
 */
export function hasBeenAnonymised(
  member: Member | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!member?.anonymisationDate) return false;
  const anon = new Date(member.anonymisationDate);
  if (Number.isNaN(anon.getTime())) return false;
  return now.getTime() >= anon.getTime();
}
